/**
 * API Utility - Shared HTTP request helpers
 * Uses XMLHttpRequest to avoid fetch interception issues in production
 */

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

/**
 * Raw GET request to full URL (used by App.js)
 */
export function xhrGet(url, headers = {}, withCredentials = true) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("GET", url, true);
    
    const token = localStorage.getItem("session_token");
    if (token) {
      xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    }
    
    Object.entries(headers).forEach(([key, value]) => {
      xhr.setRequestHeader(key, value);
    });
    xhr.withCredentials = withCredentials;
    
    xhr.onload = function() {
      try {
        const data = xhr.responseText ? JSON.parse(xhr.responseText) : null;
        resolve({ status: xhr.status, data });
      } catch (e) {
        resolve({ status: xhr.status, data: null });
      }
    };
    
    xhr.onerror = function() {
      reject(new Error("Network error"));
    };
    
    xhr.send();
  });
}

/**
 * Raw POST request to full URL (used by App.js)
 */
export function xhrPost(url, body = null, headers = {}, withCredentials = true) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", url, true);
    xhr.setRequestHeader("Content-Type", "application/json");
    
    const token = localStorage.getItem("session_token");
    if (token) {
      xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    }
    
    Object.entries(headers).forEach(([key, value]) => {
      xhr.setRequestHeader(key, value);
    });
    xhr.withCredentials = withCredentials;
    
    xhr.onload = function() {
      try {
        const data = xhr.responseText ? JSON.parse(xhr.responseText) : null;
        if (data && data.session_token) {
          localStorage.setItem("session_token", data.session_token);
        }
        resolve({ status: xhr.status, data });
      } catch (e) {
        resolve({ status: xhr.status, data: null });
      }
    };
    
    xhr.onerror = function() {
      reject(new Error("Network error"));
    };
    
    xhr.send(body ? JSON.stringify(body) : null);
  });
}

/**
 * Raw PATCH request to full URL
 */
export function xhrPatch(url, body = null, headers = {}, withCredentials = true) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PATCH", url, true);
    xhr.setRequestHeader("Content-Type", "application/json");
    
    const token = localStorage.getItem("session_token");
    if (token) {
      xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    }
    
    Object.entries(headers).forEach(([key, value]) => {
      xhr.setRequestHeader(key, value);
    });
    xhr.withCredentials = withCredentials;
    
    xhr.onload = function() {
      try {
        const data = xhr.responseText ? JSON.parse(xhr.responseText) : null;
        resolve({ status: xhr.status, data });
      } catch (e) {
        resolve({ status: xhr.status, data: null });
      }
    };
    
    xhr.onerror = function() {
      reject(new Error("Network error"));
    };
    
    xhr.send(body ? JSON.stringify(body) : null);
  });
}

/**
 * Generic request to full URL with any method (PATCH, DELETE, etc.)
 */
export function xhrRequest(url, method = "GET", body = null, headers = {}, withCredentials = true) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url, true);
    
    if (body && method !== "GET") {
      xhr.setRequestHeader("Content-Type", "application/json");
    }
    
    const token = localStorage.getItem("session_token");
    if (token) {
      xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    }
    
    Object.entries(headers).forEach(([key, value]) => {
      xhr.setRequestHeader(key, value);
    });
    xhr.withCredentials = withCredentials;
    
    xhr.onload = function() {
      try {
        const data = xhr.responseText ? JSON.parse(xhr.responseText) : null;
        resolve({ status: xhr.status, data });
      } catch (e) {
        resolve({ status: xhr.status, data: null });
      }
    };
    
    xhr.onerror = function() {
      reject(new Error("Network error"));
    };
    
    xhr.send(body ? JSON.stringify(body) : null);
  });
}

/**
 * GET request using XMLHttpRequest
 */
export function apiGet(endpoint, headers = {}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("GET", `${API}${endpoint}`, true);
    
    // Send auth token via header for cross-domain requests
    const token = localStorage.getItem("session_token");
    if (token) {
      xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    }
    
    Object.entries(headers).forEach(([key, value]) => {
      xhr.setRequestHeader(key, value);
    });
    xhr.withCredentials = true;
    
    xhr.onload = function() {
      try {
        const data = xhr.responseText ? JSON.parse(xhr.responseText) : null;
        resolve({ status: xhr.status, data, ok: xhr.status >= 200 && xhr.status < 300 });
      } catch (e) {
        resolve({ status: xhr.status, data: null, ok: false });
      }
    };
    
    xhr.onerror = function() {
      reject(new Error("Network error. Please check your connection."));
    };
    
    xhr.send();
  });
}

/**
 * POST request using XMLHttpRequest
 */
export function apiPost(endpoint, body = null, headers = {}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API}${endpoint}`, true);
    xhr.setRequestHeader("Content-Type", "application/json");
    
    // Send auth token via header for cross-domain requests
    const token = localStorage.getItem("session_token");
    if (token) {
      xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    }
    
    Object.entries(headers).forEach(([key, value]) => {
      xhr.setRequestHeader(key, value);
    });
    xhr.withCredentials = true;
    
    xhr.onload = function() {
      try {
        const data = xhr.responseText ? JSON.parse(xhr.responseText) : null;
        // Store session token if returned
        if (data && data.session_token) {
          localStorage.setItem("session_token", data.session_token);
        }
        resolve({ status: xhr.status, data, ok: xhr.status >= 200 && xhr.status < 300 });
      } catch (e) {
        resolve({ status: xhr.status, data: null, ok: false });
      }
    };
    
    xhr.onerror = function() {
      reject(new Error("Network error. Please check your connection."));
    };
    
    xhr.send(body ? JSON.stringify(body) : null);
  });
}

/**
 * PUT request using XMLHttpRequest
 */
export function apiPut(endpoint, body = null, headers = {}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", `${API}${endpoint}`, true);
    xhr.setRequestHeader("Content-Type", "application/json");
    
    const token = localStorage.getItem("session_token");
    if (token) {
      xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    }
    
    Object.entries(headers).forEach(([key, value]) => {
      xhr.setRequestHeader(key, value);
    });
    xhr.withCredentials = true;
    
    xhr.onload = function() {
      try {
        const data = xhr.responseText ? JSON.parse(xhr.responseText) : null;
        resolve({ status: xhr.status, data, ok: xhr.status >= 200 && xhr.status < 300 });
      } catch (e) {
        resolve({ status: xhr.status, data: null, ok: false });
      }
    };
    
    xhr.onerror = function() {
      reject(new Error("Network error. Please check your connection."));
    };
    
    xhr.send(body ? JSON.stringify(body) : null);
  });
}

/**
 * DELETE request using XMLHttpRequest
 */
export function apiDelete(endpoint, headers = {}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("DELETE", `${API}${endpoint}`, true);
    
    const token = localStorage.getItem("session_token");
    if (token) {
      xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    }
    
    Object.entries(headers).forEach(([key, value]) => {
      xhr.setRequestHeader(key, value);
    });
    xhr.withCredentials = true;
    
    xhr.onload = function() {
      try {
        const data = xhr.responseText ? JSON.parse(xhr.responseText) : null;
        resolve({ status: xhr.status, data, ok: xhr.status >= 200 && xhr.status < 300 });
      } catch (e) {
        resolve({ status: xhr.status, data: null, ok: false });
      }
    };
    
    xhr.onerror = function() {
      reject(new Error("Network error. Please check your connection."));
    };
    
    xhr.send();
  });
}

// Helper to get auth headers
export function getAuthHeaders() {
  const token = localStorage.getItem("session_token");
  return token ? { "Authorization": `Bearer ${token}` } : {};
}

// Helper to clear session
export function clearSession() {
  localStorage.removeItem("session_token");
}

// Helper to check if user is authenticated
export function isAuthenticated() {
  return !!localStorage.getItem("session_token");
}
